import { useMemo } from 'react';

export interface FieldConfig {
  required?: boolean;
  /** Must return only hardcoded error strings. Do not interpolate user input —
   *  error messages are rendered directly into the DOM as React children. */
  validate?: (value: unknown) => string | undefined;
  /** Wizard step this field belongs to. Undefined means the field is global (applies to all steps). */
  step?: number;
}

interface UseFormErrorsReturn<K extends string = string> {
  errors: Record<K, string | undefined>;
  isValid: boolean;
}

export function useFormErrors<T extends Record<string, unknown>>({
  fields,
  values,
}: {
  fields: { [K in keyof T]: FieldConfig };
  values: T;
}): UseFormErrorsReturn<keyof T & string> {
  const errors = useMemo<Record<string, string | undefined>>(() => {
    const result: Record<string, string | undefined> = {};
    for (const [name, config] of Object.entries(fields)) {
      const value = values[name];
      const isEmpty =
        value === undefined ||
        value === null ||
        (typeof value === 'string' ? value.trim() === '' : value === '');
      if (config.required && isEmpty) {
        result[name] = 'Required';
      } else {
        result[name] = config.validate ? config.validate(value) : undefined;
      }
    }
    return result;
  }, [fields, values]);

  const isValid = useMemo(() => Object.values(errors).every(e => !e), [errors]);

  return { errors, isValid } as unknown as UseFormErrorsReturn<
    keyof T & string
  >;
}
